import { Component, effect, EventEmitter, forwardRef, Input, Output, signal } from '@angular/core';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '@vendasta/galaxy/filter/chips';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CompanyDisplayName, CompanyFiltersService } from '../../../shared-services';

@Component({
  template: `
    <glxy-form-field bottomSpacing="'none'" *ngIf="companyDetails$ | async as companyDetails; else loading">
      <input
        [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
        type="text"
        matInput
        [formControl]="formControl"
        [matAutocomplete]="auto"
        data-testid="filter-custom-company-input"
      />
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn(companyDetails)">
        <mat-option *ngFor="let option of availableOptions()" [value]="option">
          {{ option?.displayName }}
        </mat-option>
      </mat-autocomplete>
    </glxy-form-field>
    <ng-template #loading>
      <div>
        <glxy-form-field bottomSpacing="'none'">
          <input type="text" matInput [disabled]="true" />
        </glxy-form-field>
      </div>
    </ng-template>
  `,
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => CompanyCustomFilterInputComponent),
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
  ],
  standalone: true,
})
export class CompanyCustomFilterInputComponent implements GalaxyAbstractFilterInput {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }
  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<CompanyDisplayName | string> = new FormControl<CompanyDisplayName | string>('', {
    nonNullable: true,
  });

  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });

  _fieldId = signal('');
  availableOptions$ = toObservable(this.searchTerm).pipe(
    takeUntilDestroyed(),
    switchMap((searchTerm) => {
      const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.crmObjectId;

      return this.companyFiltersService.listReadableCompanies(filterTerm || '');
    }),
  );

  availableOptions = toSignal(this.availableOptions$);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length > 0 && value[0].string) {
      this.formControl.setValue(value[0].string);
    }
  }

  clearValue(): void {
    this.formControl.setValue('');
  }

  companyDetails$ = toObservable(this.searchTerm).pipe(
    switchMap((value) => {
      let crmObjectId = '';
      if (typeof value === 'string') {
        crmObjectId = value;
      } else {
        crmObjectId = value.crmObjectId ?? '';
      }
      if (!crmObjectId) {
        return of({} as CompanyDisplayName[]);
      }
      return this.companyFiltersService.getMultiCompany([crmObjectId]);
    }),
  );

  constructor(private companyFiltersService: CompanyFiltersService) {
    effect(
      () => {
        const value = this.searchTerm();

        if (value === '') {
          this.valueChanges.emit([]);
          return;
        }
        if (typeof value === 'string') {
          this.valueChanges.emit([{ string: value }]);
          return;
        }
        this.valueChanges.emit([{ string: value?.crmObjectId }]);
      },
      { allowSignalWrites: true },
    );
  }

  displayFn(availableOptions: CompanyDisplayName[]): (company: CompanyDisplayName | string) => string {
    return (company: CompanyDisplayName | string): string => {
      if (!company || !availableOptions) {
        return '';
      }
      if (typeof company === 'string') {
        const foundCompany = availableOptions.find((displayName) => displayName.crmObjectId === company);
        return foundCompany?.displayName || company;
      }
      return company?.displayName;
    };
  }
}
